import {
  AuditTrailInput,
  OfferSummaryCount,
  CancelledOfferFilters,
} from "graphql/__generated__/globalTypes";
import {
  FetchOfferParticipationTransactionThread,
  FetchOfferParticipationTransactionThreadVariables,
} from "graphql/queries/__generated__/FetchOfferParticipationTransactionThread";
import {
  FetchPublishedOfferAuditTrailQuery,
  FetchPublishedOfferAuditTrailQueryVariables,
  FetchReinsurerRepresentativeLoyaltyPointsQuery,
  FetchUnreadTransactionThreadMessagesForReinsurerQuery,
  GetPublishedOffersQuery,
  GetPublishedOffersQueryVariables,
  GetCancelledOffersQuery,
  GetCancelledOffersQueryVariables,
} from "graphql/queries/__generated__/publish-offers";
import {
  fetchOfferParticipationTransactionThread,
  fetchOfferSummaryCount,
  fetchPublishedOfferAuditTrail,
  fetchReinsurerRepresentativeLoyaltyPoints,
  fetchUnreadTransactionThreadMessagesForReinsurer,
  getPublishedOffers,
  getCancelledOffers,
} from "graphql/queries/publish-offers";

import { useQuery } from "@apollo/client";

export const usePublishedOffers = (
  variables?: Partial<GetPublishedOffersQueryVariables>
) => {
  const {
    OfferStatus,
    searchTerm,
    filter_status,
    limit = 10,
    offset = 0,
  } = variables || {};

  const { data, refetch, ...rest } = useQuery<
    GetPublishedOffersQuery,
    GetPublishedOffersQueryVariables
  >(getPublishedOffers, {
    variables: {
      OfferStatus: OfferStatus === undefined ? "" : OfferStatus,
      searchTerm: searchTerm === undefined ? "" : searchTerm,
      filter_status: filter_status || "",
      limit,
      offset,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const refresh = refetch;

  return {
    publishedOffers: data?.getPublishedOffers || [],
    refresh,
    ...rest,
  };
};

export const useTransactionThread = (offer_published_participant: string, published_by: string) => {
  const { data, refetch, ...rest } = useQuery<
    FetchOfferParticipationTransactionThread,
    FetchOfferParticipationTransactionThreadVariables
  >(fetchOfferParticipationTransactionThread, {
    variables: {
      offer_published_participant,
      published_by,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const refresh = () => refetch();

  return {
    transactionThread: data?.fetchOfferParticipationTransactionThread || null,
    refresh,
    ...rest,
  };
};

export const useUnreadTransactionThreads = () => {
  const { data, refetch, ...rest } =
    useQuery<FetchUnreadTransactionThreadMessagesForReinsurerQuery>(
      fetchUnreadTransactionThreadMessagesForReinsurer,
      {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
      }
    );

  const refresh = () => refetch();

  return {
    unreadThreads: data?.fetchUnreadTransactionThreadMessagesForReinsurer || [],
    refresh,
    ...rest,
  };
};

export interface FetchOfferSummaryCountQuery {
  fetchOfferSummaryCount: OfferSummaryCount;
}
export const useOfferSummaryCount = (
  reinsurer_id?: number,
  search_term?: string
) => {
  const { data, refetch, ...rest } = useQuery<FetchOfferSummaryCountQuery>(
    fetchOfferSummaryCount,
    {
      variables: { reinsurer_id, search_term },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
    }
  );

  const refresh = () => refetch();

  return {
    offerSummaryCount: data?.fetchOfferSummaryCount || {
      missed_offers: 0,
      todays_offers: 0,
    },
    refresh,
    ...rest,
  };
};

export const useReinsurerRepresentativeLoyaltyPoints = () => {
  const { data, refetch, ...rest } =
    useQuery<FetchReinsurerRepresentativeLoyaltyPointsQuery>(
      fetchReinsurerRepresentativeLoyaltyPoints,
      {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
      }
    );

  const refresh = () => refetch();

  return {
    loyaltyPoints: data?.fetchReinsurerRepresentativeLoyaltyPoints || {
      total_loyalty_points: 0,
      total_redeemable_loyalty_points: 0,
    },
    refresh,
    ...rest,
  };
};

export const usePublishedOfferAuditTrail = (input: AuditTrailInput) => {
  const { data, refetch, ...rest } = useQuery<
    FetchPublishedOfferAuditTrailQuery,
    FetchPublishedOfferAuditTrailQueryVariables
  >(fetchPublishedOfferAuditTrail, {
    variables: { input },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const refresh = () => refetch();

  return {
    auditTrail: data?.fetchPublishedOfferAuditTrail || null,
    refresh,
    ...rest,
  };
};

export const useCancelledOffers = (
  filters?: Partial<CancelledOfferFilters>
) => {
  const { data, refetch, ...rest } = useQuery<
    GetCancelledOffersQuery,
    GetCancelledOffersQueryVariables
  >(getCancelledOffers, {
    variables: {
      filters: filters as CancelledOfferFilters,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const refresh = refetch;

  return {
    cancelledOffers: data?.getCancelledOffers || [],
    refresh,
    ...rest,
  };
};
